import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { from, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, flatMap, map, scan, share, switchMap, tap } from 'rxjs/operators';
import { ShortDatePipe } from '../../../shared/pipes/short-date.pipe';
import { CustomerSearchService } from '../../shared/customer-search.service';

const MAX_RESULTS_TO_DISPLAY = 250;

@Component({
    selector: 'customer-search-results',
    templateUrl: './search-results.html',
})
export class CustomerSearchResultsComponent implements OnChanges, OnDestroy, OnInit {
    @Input() data: any;
    @Input() showSelect = false;
    @Input() enableAutoSelect = true;
    @Output() onCustomerSelected = new EventEmitter<number>();
    @Output() onCustomerNameClicked = new EventEmitter<number>();

    public showResults = false;
    public resultTotalCount: number;
    private event: any;
    private showNames$ = new Subject<any>();
    private showNamesSubscription: Subscription;

    constructor(
        private customerSearchService: CustomerSearchService,
        private shortDatePipe: ShortDatePipe
    ) {
    }

    public ngOnInit(): void {
        this.setupShowNamesListener();
    }

    public ngOnChanges(): void {
        const nonZeroResults = !!this.data && !!this.data.results && this.data.results.length > 0;
        const tooManyResults = nonZeroResults && this.data.results.length >= MAX_RESULTS_TO_DISPLAY;
        const oneResult = nonZeroResults && this.data.results.length === 1;
        this.showResults = nonZeroResults && !tooManyResults;
        this.resultTotalCount = nonZeroResults ? this.data.results.length : 0;

        if (oneResult && this.enableAutoSelect) {
            this.displayCustomer();
        } else if (nonZeroResults && !tooManyResults) {
            this.setSortIdentification();
            this.convertDOB();
        }
    }

    public ngOnDestroy(): void {
        if (this.showNamesSubscription) {
            this.showNamesSubscription.unsubscribe();
        }
    }

    public hideNames(event: any) {
        if (event.data) {
            event.data.akas = [];
            event.data.dbas = [];
        }
    }

    public showCustomer(customerNumberId: number): void {
        this.onCustomerNameClicked.emit(customerNumberId);
    }

    public selectCustomer(customerNumberId: number): void {
        this.onCustomerSelected.emit(customerNumberId);
    }

    public showNames(event: any) {
        this.event = event;
        this.hideNames(this.event);
        this.showNames$.next(event);
    }

    public setupShowNamesListener() {
        const getNames$ = this.showNames$
            .pipe(
                switchMap(event => this.getNamesForCustomer.call(this, event)),
                share()
            );
        const akas$ = getNames$
            .pipe(switchMap((names: any[]) => this.collectAkaNames.call(this, names)));
        const dbas$ = getNames$
            .pipe(switchMap((names: any[]) => this.collectDbaNames.call(this, names)));

        this.showNamesSubscription = merge(akas$, dbas$)
            .subscribe(() => {}, e => console.log('error', e));
    }

    public trimAddress(address: string): string {
        const longEnoughString = !!address && address.length >= 8;
        return longEnoughString ? `${address.substring(0, 8)}...` : address;
    }

    private collectAkaNames(names: any[]): Observable<any> {
        return of(names).pipe(
            flatMap(names => from(names)),
            filter((name: any) => +name.customerNameTypeId === 7),
            map(name => name.firstName + (name.middleName != null ? ' ' + name.middleName : '') + ' ' + name.lastName),
            scan((akaNameArray, fullName) => [...akaNameArray, fullName], []),
            tap(akaNameArray => this.event.data.akas = akaNameArray)
        );
    }

    private collectDbaNames(names: any[]): Observable<any[]> {
        return of(names).pipe(
            flatMap(names => from(names)),
            filter((name: any) => +name.customerNameTypeId === 6),
            map(name => name.organizationName),
            scan((dbaNameArray, orgName) => [...dbaNameArray, orgName], []),
            tap(dbaNameArray => this.event.data.dbas = dbaNameArray)
        );
    }

    private displayCustomer() {
        const firstCustomerNumber = this.data.results[0].customerNumber;
        this.showSelect ? this.selectCustomer(firstCustomerNumber) : this.showCustomer(firstCustomerNumber);
    }

    private getNamesForCustomer(event: any): Observable<any[]> {
        return of(event).pipe(
            filter(event => event.data && event.data.customerNumber),
            map(event => event.data.customerNumber),
            switchMap(customerNumber => this.customerSearchService.getNames(customerNumber)),
            filter(result => result && result.length > 0)
        );
    }

    private setSortIdentification() {
        this.data.results.forEach(x => {
            x.sortIdentification = null;
            if (x.customerTypeId == 1 && x.ssn) {
                x.sortIdentification = x.ssn;
            } else if (x.customerTypeId == 2) {
                x.sortIdentification = '*' + (x.ein ? x.ein : x.oin);
            }
        });
    }

    private convertDOB() {
        this.data.results
            .filter((result: any) => !!result.dateOfBirth)
            .forEach((result: any) => result.dateOfBirth = this.shortDatePipe.transform(result.dateOfBirth));
    }
}
